"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { Dialog } from "~/components/ui/dialog";
import { useMediaQuery } from "~/lib/hooks/use-media-query";
import { useSheetDrag } from "~/lib/hooks/use-sheet-drag";
import {
  clearAllFilters,
  parseFilterParams,
} from "~/lib/storefront/filter-params";
import { api } from "~/trpc/react";
import type { FacetGroupModel } from "./facet-model";
import { FacetGroups } from "./facet-groups";
import { createFilterHandlers } from "./filter-handlers";
import { useTranslator } from "~/i18n/provider";

/**
 * P2-11 · Mobile filter drawer. Focus-trapped dialog whose facet changes stage
 * locally (no URL churn while browsing); a sticky bar shows the live result
 * count for the staged set and commits it on "Show N results" (P2-DB02 §05).
 * Shown only below the PLP breakpoint.
 */
export function FilterDrawer({
  groups,
  currency,
  categoryId,
  searchQuery,
  activeCount,
}: {
  groups: FacetGroupModel[];
  currency: string;
  /** Category mode — the live staged count reads `products.list`. */
  categoryId?: string;
  /** Search mode (P3-16) — the live staged count reads `search.results`. */
  searchQuery?: string;
  activeCount: number;
}) {
  const t = useTranslator();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const committed = params.toString();

  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState(committed);

  // P9-14 · Drag-to-dismiss (phones). Reduced motion keeps the direct
  // manipulation — that's not an animation — but drops the spring-back.
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const drag = useSheetDrag({ onDismiss: () => setOpen(false) });

  // Re-sync staged state to the URL whenever the drawer (re)opens.
  useEffect(() => {
    if (open) setStaged(committed);
  }, [open, committed]);

  const handlers = createFilterHandlers(staged, setStaged, currency);

  // Live count for the staged set — only while the drawer is open. Category
  // and search modes read different endpoints; both hooks are called (rules of
  // hooks) but only the active one is enabled.
  const stagedFilters = parseFilterParams(new URLSearchParams(staged));
  const isSearch = searchQuery !== undefined;
  const categoryCount = api.products.list.useQuery(
    { categoryId: categoryId ?? "", filters: stagedFilters, pageSize: 1 },
    { enabled: open && !isSearch, staleTime: 10_000 },
  );
  const searchCount = api.search.results.useQuery(
    { q: searchQuery ?? "", filters: stagedFilters, pageSize: 1 },
    { enabled: open && isSearch, staleTime: 10_000 },
  );
  const count = isSearch ? searchCount : categoryCount;

  const apply = () => {
    router.push(staged ? `${pathname}?${staged}` : pathname, { scroll: false });
    setOpen(false);
  };

  if (groups.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-fg lg:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          className="h-4 w-4"
        >
          <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
        {t("browse.filters")}
        {activeCount > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-fg">
            {activeCount}
          </span>
        )}
      </button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        placement="right"
        className="flex w-full flex-col p-0 sm:max-w-sm"
        panelStyle={
          // P9-14 · Track the finger 1:1 while dragging (no transition), then
          // let it spring back on release. Transform only — never a layout
          // property — so the drag stays on the compositor.
          drag.offset > 0 || drag.dragging
            ? {
                transform: `translateY(${drag.offset}px)`,
                transition:
                  drag.dragging || reducedMotion
                    ? "none"
                    : "transform 200ms ease-out",
              }
            : undefined
        }
      >
        {/* P9-14 · Drag-to-dismiss grip. Phones only: above `sm` the panel is a
            full-height side drawer, where a downward drag means nothing. */}
        <div
          {...drag.handleProps}
          aria-hidden="true"
          className="flex shrink-0 cursor-grab touch-none justify-center py-3 active:cursor-grabbing sm:hidden"
        >
          <span className="h-1.5 w-10 rounded-full bg-border" />
        </div>
        <div className="flex items-center justify-between border-b border-border px-4 pb-3 sm:pt-3">
          <h2 className="font-heading text-lg font-semibold">
            {t("browse.filters")}
          </h2>
          <button
            type="button"
            aria-label={t("browse.closeFilters")}
            onClick={() => setOpen(false)}
            className="flex size-11 items-center justify-center rounded-md text-fg-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <FacetGroups groups={groups} handlers={handlers} />
        </div>

        {/* P9-14 · Thumb-reachable action bar: ≥44px targets, clear of the home
            indicator via the safe-area inset. */}
        <div className="flex items-center gap-3 border-t border-border px-4 py-3 [padding-bottom:calc(0.75rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={() => {
              setStaged(clearAllFilters(staged));
            }}
            className="flex min-h-11 items-center px-1 text-sm text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {t("browse.clearAll")}
          </button>
          <button
            type="button"
            onClick={apply}
            className="ml-auto min-h-11 flex-1 rounded-[var(--btn-radius)] bg-primary px-4 py-2.5 text-sm text-primary-fg [font-weight:var(--btn-weight)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {count.isSuccess
              ? `Show ${count.data.total} results`
              : "Show results"}
          </button>
        </div>
      </Dialog>
    </>
  );
}
